import { useMemo, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, CartesianGrid, Legend } from "recharts";
import api, { API } from "@/lib/api";
import { ROUTE_TYPE_META, RouteBadge } from "@/lib/routeTypes";
import { UserCircle2, Users, Download, ArrowUpRight, Trash2, Pencil, Search, BarChart3, X } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

function slug(s) { return String(s || "").replace(/\s+/g, "-").toLowerCase(); }

// Monday (in UTC) of the given yyyy-mm-dd string, returned as yyyy-mm-dd.
function mondayOf(dateStr) {
  const d = dateStr ? new Date(`${dateStr}T00:00:00Z`) : new Date();
  const wd = (d.getUTCDay() + 6) % 7; // Mon=0..Sun=6
  d.setUTCDate(d.getUTCDate() - wd);
  return d.toISOString().slice(0, 10);
}

function currentMonday() { return mondayOf(new Date().toISOString().slice(0, 10)); }

const COMPARE_TYPES = ["small", "big", "pickup", "walmart", "split"];

async function downloadDriverExcel(driverName) {
  const res = await fetch(`${API}/export/driver/${encodeURIComponent(driverName)}`, {
    credentials: "include",
  });
  if (!res.ok) {
    toast.error("Download failed");
    return;
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `driver_${driverName.replace(/\s+/g, "_")}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success(`Downloaded ${driverName}.xlsx`);
}

function RenameDriverDialog({ driverName, onRenamed }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(driverName);
  const [busy, setBusy] = useState(false);
  async function save() {
    setBusy(true);
    try {
      await api.patch(`/drivers/${encodeURIComponent(driverName)}/rename`, { new_name: name });
      toast.success(driverName === name ? "No change" : `Renamed to ${name}`);
      qc.invalidateQueries();
      setOpen(false);
      onRenamed && onRenamed(name);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`driver-rename-${slug(driverName)}`} className="inline-flex items-center gap-2 px-4 py-2 border border-[#E2E2E8] hover:bg-slate-50 text-sm font-medium rounded-md">
          <Pencil className="w-4 h-4" /> Rename
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename or merge driver</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">
          To merge two drivers, rename this one to the exact spelling of the other. All entries will roll up under the new name.
        </p>
        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="driver-rename-input" className="w-full px-3 py-2 border border-[#E2E2E8] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <DialogFooter>
          <button onClick={() => setOpen(false)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={save} disabled={busy || !name.trim()} data-testid="driver-rename-save" className="px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md disabled:opacity-60">
            {busy ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDriverButton({ driverName, size = "sm", label = false, onDeleted }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  async function doDelete() {
    setBusy(true);
    try {
      const { data } = await api.delete(`/drivers/${encodeURIComponent(driverName)}`);
      toast.success(`Removed ${driverName} · ${data.deleted} entries deleted`);
      qc.invalidateQueries();
      onDeleted && onDeleted();
    } catch (e) {
      toast.error("Delete failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
      setOpen(false);
    }
  }

  const cls = size === "lg"
    ? "inline-flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium rounded-md transition-colors duration-150"
    : "inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-sm transition-colors duration-150";

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button
          data-testid={`driver-delete-${slug(driverName)}${size === "lg" ? "" : "-row"}`}
          className={cls}
          onClick={(e) => e.stopPropagation()}
        >
          <Trash2 className={size === "lg" ? "w-4 h-4" : "w-3.5 h-3.5"} />
          {label ? <span>Delete driver</span> : size === "sm" ? <span>delete</span> : null}
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {driverName}?</AlertDialogTitle>
          <AlertDialogDescription>
            This removes {driverName}'s tab and every route entry linked to them (all 60 days). This can't be undone — download their Excel first if you need a backup.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`driver-delete-cancel-${slug(driverName)}`}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid={`driver-delete-confirm-${slug(driverName)}`}
            disabled={busy}
            onClick={doDelete}
            className="bg-red-600 hover:bg-red-700"
          >
            <Trash2 className="w-4 h-4 mr-1" /> {busy ? "Deleting..." : "Delete driver"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function AllDriversView({ drivers, onOpen, search, setSearch, weekStart, setWeekStart, compareMode, setCompareMode, selected, toggleSelected, onCompareOpen }) {
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((d) => d.driver_name.toLowerCase().includes(q));
  }, [drivers, search]);

  const selectedCount = selected.length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="driver-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search drivers…"
            className="w-full pl-9 pr-3 py-2 border border-[#E2E2E8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Week</label>
          <input
            type="date"
            data-testid="driver-week-input"
            value={weekStart}
            onChange={(e) => setWeekStart(mondayOf(e.target.value))}
            className="px-3 py-2 border border-[#E2E2E8] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono-data"
          />
          <button
            data-testid="driver-week-reset"
            onClick={() => setWeekStart(currentMonday())}
            className="px-3 py-2 text-xs text-slate-600 hover:bg-slate-100 rounded-md"
          >This week</button>
        </div>
        <button
          data-testid="driver-compare-toggle"
          onClick={() => { setCompareMode(!compareMode); }}
          className={`ml-auto inline-flex items-center gap-2 px-3 py-2 border rounded-md text-sm font-medium transition-colors duration-150 ${
            compareMode ? "border-[#2563EB] bg-blue-50 text-[#2563EB]" : "border-[#E2E2E8] text-slate-700 hover:bg-slate-50"
          }`}
        >
          <BarChart3 className="w-4 h-4" /> {compareMode ? "Cancel compare" : "Compare"}
        </button>
        {compareMode && (
          <button
            data-testid="driver-compare-open"
            disabled={selectedCount < 2}
            onClick={onCompareOpen}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md"
          >
            Compare ({selectedCount}/10)
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg overflow-hidden">
        <div className="grid grid-cols-12 px-5 py-3 border-b border-[#E2E2E8] bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
          <div className={compareMode ? "col-span-3 flex items-center gap-2" : "col-span-3"}>
            {compareMode && <span className="w-4" />}
            Driver
          </div>
          <div className="col-span-1 text-right">Small</div>
          <div className="col-span-1 text-right">Big</div>
          <div className="col-span-1 text-right">Pickup</div>
          <div className="col-span-1 text-right">Walmart</div>
          <div className="col-span-1 text-right">Split</div>
          <div className="col-span-1 text-right">Week</div>
          <div className="col-span-1 text-right">60d</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        {filtered.length === 0 ? (
          <div className="p-6 text-sm text-slate-500" data-testid="driver-empty-state">
            {search ? `No drivers match "${search}".` : "No drivers yet. Paste your first roster on the Paste tab."}
          </div>
        ) : (
          filtered.map((d) => {
            const isChecked = selected.includes(d.driver_name);
            const disabled = compareMode && !isChecked && selected.length >= 10;
            return (
              <div
                key={d.driver_name}
                data-testid={`driver-row-${d.driver_name}`}
                className={`grid grid-cols-12 items-center px-5 py-3 border-b border-[#E2E2E8] last:border-b-0 transition-colors duration-150 ${
                  isChecked ? "bg-blue-50/40" : "hover:bg-slate-50"
                }`}
              >
                <div className="col-span-3 flex items-center gap-2">
                  {compareMode && (
                    <input
                      type="checkbox"
                      data-testid={`driver-compare-check-${slug(d.driver_name)}`}
                      checked={isChecked}
                      disabled={disabled}
                      onChange={() => toggleSelected(d.driver_name)}
                      className="w-4 h-4 accent-[#2563EB]"
                    />
                  )}
                  <button
                    onClick={() => onOpen(d.driver_name)}
                    data-testid={`driver-open-${slug(d.driver_name)}`}
                    className="text-left text-sm font-medium text-[#2563EB] hover:underline flex items-center gap-1"
                  >
                    {d.driver_name} <ArrowUpRight className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="col-span-1 text-right font-mono-data text-sm">{d.week_counts.small || 0}</div>
                <div className="col-span-1 text-right font-mono-data text-sm">{d.week_counts.big || 0}</div>
                <div className="col-span-1 text-right font-mono-data text-sm">{d.week_counts.pickup || 0}</div>
                <div className="col-span-1 text-right font-mono-data text-sm">{d.week_counts.walmart || 0}</div>
                <div className="col-span-1 text-right font-mono-data text-sm">{d.week_counts.split || 0}</div>
                <div className="col-span-1 text-right font-mono-data text-sm font-semibold text-[#2563EB]">{d.week_total}</div>
                <div className="col-span-1 text-right font-mono-data text-sm text-slate-500">{d.total}</div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <button
                    onClick={() => downloadDriverExcel(d.driver_name)}
                    data-testid={`driver-download-row-${slug(d.driver_name)}`}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-[#2563EB] hover:bg-blue-50 rounded-sm transition-colors duration-150"
                  >
                    <Download className="w-3.5 h-3.5" /> xlsx
                  </button>
                  <DeleteDriverButton driverName={d.driver_name} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function CompareDialog({ open, onOpenChange, driverNames, weekStart }) {
  const enabled = open && driverNames.length >= 2;
  const { data, isLoading } = useQuery({
    queryKey: ["compare-drivers", driverNames.join(","), weekStart],
    queryFn: async () => (await api.get(`/compare/drivers?drivers=${encodeURIComponent(driverNames.join(","))}&week_start=${weekStart}`)).data,
    enabled,
  });

  const chartData = useMemo(() => {
    if (!data) return [];
    return data.drivers.map((d) => {
      const row = { driver: d.driver_name };
      for (const t of COMPARE_TYPES) row[t] = d.counts[t] || 0;
      row.total = d.total;
      return row;
    });
  }, [data]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#2563EB]" />
            Compare drivers · week of {weekStart}
          </DialogTitle>
        </DialogHeader>
        {isLoading || !data ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-4" data-testid="compare-dialog-body">
            <div style={{ width: "100%", height: 300 }}>
              <ResponsiveContainer>
                <BarChart data={chartData} margin={{ top: 8, right: 16, left: -8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                  <XAxis dataKey="driver" tick={{ fontSize: 11, fill: "#0F172A" }} interval={0} angle={-15} textAnchor="end" height={60} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {COMPARE_TYPES.map((t) => (
                    <Bar key={t} dataKey={t} stackId="a" name={ROUTE_TYPE_META[t].label} fill={ROUTE_TYPE_META[t].text} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="border border-[#E2E2E8] rounded-md overflow-hidden">
              <div className="grid grid-cols-8 px-4 py-2 border-b border-[#E2E2E8] bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
                <div className="col-span-2">Driver</div>
                {COMPARE_TYPES.map((t) => (
                  <div key={t} className="text-right">{ROUTE_TYPE_META[t].label}</div>
                ))}
                <div className="text-right">Total</div>
              </div>
              {data.drivers.map((d) => (
                <div key={d.driver_name} data-testid={`compare-row-${slug(d.driver_name)}`} className="grid grid-cols-8 px-4 py-2 border-b border-[#E2E2E8] last:border-b-0 text-sm">
                  <div className="col-span-2 font-medium">{d.driver_name}</div>
                  {COMPARE_TYPES.map((t) => (
                    <div key={t} className="text-right font-mono-data">{d.counts[t] || 0}</div>
                  ))}
                  <div className="text-right font-mono-data font-semibold text-[#2563EB]">{d.total}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        <DialogFooter>
          <button data-testid="compare-close" onClick={() => onOpenChange(false)} className="inline-flex items-center gap-2 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md">
            <X className="w-4 h-4" /> Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DriverProfile({ driver }) {
  const meta = (t) => ROUTE_TYPE_META[t] || ROUTE_TYPE_META.other;
  const stats = [
    { key: "small", label: "Small" },
    { key: "big", label: "Big" },
    { key: "pickup", label: "Pickup" },
    { key: "walmart", label: "Walmart" },
    { key: "split", label: "Split" },
  ];

  return (
    <div className="space-y-6" data-testid={`driver-profile-${driver.driver_name}`}>
      <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg p-6">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
            <UserCircle2 className="w-7 h-7 text-[#2563EB]" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Driver</div>
            <h2 className="font-heading text-3xl font-semibold tracking-tight">{driver.driver_name}</h2>
          </div>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-right">
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Week Total</div>
              <div className="font-heading text-4xl font-semibold text-[#2563EB] mt-1">{driver.week_total}</div>
            </div>
            <button
              onClick={() => downloadDriverExcel(driver.driver_name)}
              data-testid={`driver-download-${slug(driver.driver_name)}`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm font-medium rounded-md transition-colors duration-150"
            >
              <Download className="w-4 h-4" /> Download
            </button>
            <RenameDriverDialog driverName={driver.driver_name} />
            <DeleteDriverButton driverName={driver.driver_name} size="lg" label />
          </div>
        </div>
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3">
          {stats.map((s) => {
            const m = meta(s.key);
            return (
              <div key={s.key} className="p-4 rounded-sm border" style={{ backgroundColor: m.bg, borderColor: m.border }}>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold" style={{ color: m.text }}>{s.label}</div>
                <div className="mt-1 font-heading text-2xl font-semibold" style={{ color: m.text }}>{driver.week_counts[s.key] || 0}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white border border-[#E2E2E8] rounded-none md:rounded-lg overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E2E2E8] flex items-center justify-between">
          <h3 className="font-heading text-lg font-medium">History (last 60 days)</h3>
          <span className="text-xs text-slate-500 font-mono-data">{driver.history.length} entries</span>
        </div>
        {driver.history.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No history yet for this driver.</div>
        ) : (
          <div>
            <div className="grid grid-cols-12 px-5 py-2 border-b border-[#E2E2E8] bg-slate-50 text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">
              <div className="col-span-3">Date</div>
              <div className="col-span-1">Wave</div>
              <div className="col-span-3">Route</div>
              <div className="col-span-3">Van</div>
              <div className="col-span-2">Type</div>
            </div>
            {driver.history.map((h) => (
              <div key={`${h.date}-${h.route_code}`} className="grid grid-cols-12 px-5 py-2.5 border-b border-[#E2E2E8] last:border-b-0 items-center hover:bg-slate-50 transition-colors duration-150">
                <div className="col-span-3 font-mono-data text-sm text-slate-600">{h.date?.slice(0, 10)}</div>
                <div className="col-span-1 font-mono-data text-xs text-slate-500">{h.wave || "—"}</div>
                <div className="col-span-3 font-mono-data text-sm">{h.route_code}</div>
                <div className="col-span-3 font-mono-data text-sm text-slate-600">{h.van_code}</div>
                <div className="col-span-2"><RouteBadge type={h.route_type} /></div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Drivers() {
  const [search, setSearch] = useState("");
  const [weekStart, setWeekStart] = useState(currentMonday());
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [compareOpen, setCompareOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["drivers", weekStart],
    queryFn: async () => (await api.get(`/drivers?week_start=${weekStart}`)).data,
  });
  const drivers = useMemo(() => data?.drivers || [], [data]);
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("driver") || "__all__";

  const setActive = (name) => {
    if (name === "__all__") setSearchParams({});
    else setSearchParams({ driver: name });
  };

  const toggleSelected = (name) => {
    setSelected((prev) => {
      if (prev.includes(name)) return prev.filter((n) => n !== name);
      if (prev.length >= 10) { toast.error("Max 10 drivers"); return prev; }
      return [...prev, name];
    });
  };

  // Exit compare mode when switching to a specific driver
  useEffect(() => {
    if (active !== "__all__" && compareMode) { setCompareMode(false); setSelected([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Reset if driver disappears
  useEffect(() => {
    if (active !== "__all__" && drivers.length > 0 && !drivers.find((d) => d.driver_name === active)) {
      setSearchParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drivers]);

  const activeDriver = active !== "__all__" ? drivers.find((d) => d.driver_name === active) : null;

  return (
    <div className="p-6 md:p-8 max-w-7xl">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Registry</div>
      <h1 className="font-heading text-4xl font-semibold tracking-tight mt-1">Drivers</h1>
      <p className="text-sm text-slate-500 mt-1">
        A tab is auto-created for every driver. Click a name to see their profile, history, and per-driver Excel download.
      </p>

      <div className="mt-8 border-b border-[#E2E2E8] flex overflow-x-auto no-scrollbar">
        <button
          onClick={() => setActive("__all__")}
          data-testid="driver-tab-all"
          className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors duration-150 ${
            active === "__all__" ? "border-[#2563EB] text-[#2563EB] font-medium" : "border-transparent text-slate-600 hover:text-slate-900"
          }`}
        >
          <Users className="w-4 h-4" /> All Drivers <span className="text-xs font-mono-data opacity-70">({drivers.length})</span>
        </button>
        {drivers.map((d) => {
          const isActive = active === d.driver_name;
          return (
            <button
              key={d.driver_name}
              onClick={() => setActive(d.driver_name)}
              data-testid={`driver-tab-${slug(d.driver_name)}`}
              className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors duration-150 whitespace-nowrap ${
                isActive ? "border-[#2563EB] text-[#2563EB] font-medium" : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              {d.driver_name}
              <span className="text-xs font-mono-data px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600">{d.week_total}</span>
            </button>
          );
        })}
      </div>

      <div className="mt-6 fade-in" key={active}>
        {isLoading ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : active === "__all__" ? (
          <>
            <AllDriversView
              drivers={drivers}
              onOpen={setActive}
              search={search}
              setSearch={setSearch}
              weekStart={weekStart}
              setWeekStart={setWeekStart}
              compareMode={compareMode}
              setCompareMode={(v) => { setCompareMode(v); if (!v) setSelected([]); }}
              selected={selected}
              toggleSelected={toggleSelected}
              onCompareOpen={() => setCompareOpen(true)}
            />
            <CompareDialog
              open={compareOpen}
              onOpenChange={setCompareOpen}
              driverNames={selected}
              weekStart={weekStart}
            />
          </>
        ) : activeDriver ? (
          <DriverProfile driver={activeDriver} />
        ) : (
          <div className="p-6 text-sm text-slate-500">Driver not found.</div>
        )}
      </div>
    </div>
  );
}
